import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { List, Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add, Block } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility"; // Ícone de spy
import useCompanySettings from "../../hooks/useSettings/companySettings";
import NewTicketModal from "../NewTicketModal";
import {
  Avatar,
  Badge,
  ListItemAvatar,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    margin: "12px 8px !important",
    width: "calc(100% - 16px) !important",
    boxSizing: "border-box",
    borderRadius: "16px !important",
    backgroundColor: theme.palette.type === "light" ? "#FFFFFF !important" : "#1E1E24 !important",
    boxShadow: theme.palette.type === "light" 
      ? "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04) !important" 
      : "6px 6px 14px rgba(0,0,0,0.8), -6px -6px 14px rgba(255,255,255,0.06) !important",
    border: theme.palette.type === "light" 
      ? "1px solid #E2E8F0 !important" 
      : "1px solid rgba(255,255,255,0.04) !important",
    borderBottom: "none !important",
    "&:hover": {
      backgroundColor: theme.palette.type === "light" 
        ? "#F8FAFC !important" 
        : `${theme.palette.primary.main}14 !important`,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.type === "light" 
        ? `${theme.palette.primary.main}1F !important` 
        : `${theme.palette.primary.main}1F !important`,
      boxShadow: theme.palette.type === "light"
        ? `0 2px 8px ${theme.palette.primary.main}33 !important`
        : `inset 4px 4px 8px rgba(0,0,0,0.6), 0 0 12px ${theme.palette.primary.main}33 !important`,
      border: `1px solid ${theme.palette.primary.main}66 !important`,
      borderLeft: `4px solid ${theme.palette.primary.main} !important`,
    },
  },
  importedCard: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '0 25px 25px 0',
      borderColor: 'transparent #ffb74d transparent transparent',
      borderTopRightRadius: "16px",
      zIndex: 10,
    }
  },

  pendingTicket: {
    cursor: "unset",
  },
  queueTag: {
    background: "#FCFCFC",
    color: "#000",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.5em",
    whiteSpace: "nowrap",
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 4,
    padding: "0 6px",
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.58rem",
    height: 18,
    minHeight: 18,
    maxHeight: 18,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "18px",
    whiteSpace: "nowrap",
    width: "auto",
    flexShrink: 0,
    textTransform: "uppercase",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginLeft: "5px",
    paddingRight: "80px",
    fontWeight: "bold",
    color: theme.palette.type === "light" ? "#0F172A" : "white",
  },

  lastMessageTime: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: -15,
    right: 20,
    color: theme.palette.type === "light" ? "#475569" : grey[400],
  },

  lastMessageTimeUnread: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: -15,
    color: "green",
    fontWeight: "bold",
    right: 20,
  },

  newMessagesCount: {
    display: "inline-flex",
    marginRight: 8,
    position: "relative",
    top: 0,
    right: 0,
  },

  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: "0px",
    marginLeft: "5px",
    color: theme.palette.type === "light" ? "#475569" : grey[400],
  },

  contactLastMessageUnread: {
    paddingRight: "0px",
    marginLeft: "5px",
    fontWeight: "bold",
    color: theme.palette.type === "light" ? "#475569" : grey[400],
  },

  badgeStyle: {
    color: "white",
    backgroundColor: green[500],
  },

  acceptButton: {
    position: "absolute",
    right: "15px",
  },

  ticketQueueColor: {
    flex: "none",
    height: "100%",
    position: "absolute",
    top: "0%",
    left: "0%",
  },

  ticketInfo: {
    position: "relative",
    top: -13,
  },
  secondaryContentSecond: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "nowrap",
    flexDirection: "row",
    alignContent: "flex-start",
  },
  ticketInfo1: {
    position: "relative",
    top: 13,
    right: 0,
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 2,
      position: "inherit",
      height: 16,
      margin: 2,
      padding: 3,
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },
  },
  connectionIcon: {
    marginRight: theme.spacing(1),
  },

  // Estilos para o modal da imagem
  imageModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageModalContent: {
    outline: "none",
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  expandedImage: {
    width: "100%",
    height: "auto",
    maxWidth: "500px",
    borderRadius: theme.spacing(1),
  },
  clickableAvatar: {
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  }
}));

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Estados para o modal de finalização de venda
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [finalizacaoTipo, setFinalizacaoTipo] = useState(null);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem

  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Função para abrir modal da imagem
  const handleImageClick = (e) => {
    e.stopPropagation(); // Prevenir que o clique no avatar selecione o ticket
    if (ticket?.contact?.urlPicture) {
      setImageModalOpen(true);
    }
  };

  // Função para fechar modal da imagem
  const handleImageModalClose = () => {
    setImageModalOpen(false);
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    setOpenFinalizacaoVenda(true);
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "ignored",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
        motivoFinalizacao: "ignored",
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + "...";
      }
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  }, []);

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenNewTicketModal = () => {
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket) {
      // Se um novo ticket foi criado, redirecionar para ele
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: (user?.profile === "admin" && ticket.userId) ? ticket.userId : user?.id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting.sendGreetingAccepted === "enabled" &&
          (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
        ) {
          handleSendMessage(ticket.id);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      toastError(err);
    }

    if (!setting?.greetingAcceptedMessage || !setting.greetingAcceptedMessage.trim()) {
      return; // No enviamos nada si no hay un mensaje real configurado
    }

    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (
    ticketData,
    sendFarewellMessage,
    finalizacaoMessage
  ) => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        ...ticketData,
        sendFarewellMessage,
        finalizacaoMessage,
      });
      toast.success(i18n.t("tickets.success.finished"));
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  // Função para espionar ticket chatbot
  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  // Lógica de permissão para mensagens pending - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const shouldBlurMessages = ticket.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  // Função para renderizar a mensagem com base na permissão - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const renderLastMessage = () => {
    if (shouldBlurMessages) {
      return (
        <MarkdownWrapper>
          {i18n.t("tickets.messageHidden")}
        </MarkdownWrapper>
      );
    }

    if (!ticket.lastMessage) {
      return <br />;
    }

    if (ticket.lastMessage.includes("data:image/png;base64")) {
      return <MarkdownWrapper>{i18n.t("tickets.location")}</MarkdownWrapper>;
    }

    if (ticket.lastMessage.includes("BEGIN:VCARD")) {
      return <MarkdownWrapper>{i18n.t("tickets.contact")}</MarkdownWrapper>;
    }

    if (
      /mensagem apagada/i.test(ticket.lastMessage) ||
      /mensaje eliminado/i.test(ticket.lastMessage) ||
      ticket.lastMessage.includes("deletedMessage")
    ) {
      return (
        <MarkdownWrapper>
          {`🚫 _${i18n.t("tickets.deletedMessage")}_`}
        </MarkdownWrapper>
      );
    }

    return (
      <MarkdownWrapper>
        {truncate(ticket.lastMessage, 40)}
      </MarkdownWrapper>
    );
  };

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket.contact}
        />
      )}
      <ListItem
        button
        dense
        onClick={(e) => {
          console.log("e", e);
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === "input" &&
              e.target.type === "checkbox") ||
            (e.target.tagName.toLowerCase() === "svg" &&
              e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === "path" &&
              e.target.type === undefined);

          if (isCheckboxClicked) return;

          handleSelectTicket(ticket);
        }}
        selected={ticketId && ticketId === ticket.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
          [classes.importedCard]: !!ticket.imported,
        })}
        style={{
          margin: "12px 8px",
          width: "calc(100% - 16px)",
          borderRadius: "16px",
          backgroundColor: "#292D32",
          boxShadow: "5px 5px 10px #1d2024, -5px -5px 10px #353a40",
          border: "none",
          borderBottom: "none",
          paddingLeft: "15px",
        }}
      >
        <ListItemAvatar style={{ marginLeft: "0px" }}>
          <Badge
            overlap="circular"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            badgeContent={shouldBlurMessages ? "?" : ticket.unreadMessages}
            invisible={Number(ticket.unreadMessages) === 0}
            classes={{ badge: classes.badgeStyle }}
          >
            <Avatar
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
              }}
              src={`${ticket?.contact?.urlPicture}`}
              className={classes.clickableAvatar}
              onClick={handleImageClick}
            />
          </Badge>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <span className={classes.contactNameWrapper}>
              <Typography
                noWrap
                component="span"
                variant="body2"
              >
                {ticket.isGroup && ticket.channel === "whatsapp" && (
                  <GroupIcon
                    fontSize="small"
                    style={{
                      color: grey[700],
                      marginBottom: "-1px",
                      marginLeft: "5px",
                    }}
                  />
                )}{" "}
                &nbsp;
                {ticket.channel && (
                  <ConnectionIcon
                    width="20"
                    height="20"
                    className={classes.connectionIcon}
                    connectionType={ticket?.whatsapp?.channel || ticket.channel}
                  />
                )}{" "}
                &nbsp;
                {truncate(ticket.contact?.name, 60)}
              </Typography>
            </span>
          }
          secondary={
            <span className={classes.contactNameWrapper}>
              <Typography
                className={
                  Number(ticket.unreadMessages) > 0
                    ? classes.contactLastMessageUnread
                    : classes.contactLastMessage
                }
                noWrap
                component="span"
                variant="body2"
              >
                {renderLastMessage()}
                <span
                  className={classes.secondaryContentSecond}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    overflow: "hidden"
                  }}
                >
                  {ticket?.whatsapp ? (
                    <Tooltip title={`📱 Conexión: ${ticket.whatsapp?.name || ""}`} arrow placement="top">
                      <span
                        className={classes.connectionTag}
                        style={{
                          backgroundColor:
                            ticket.channel === "whatsapp" || ticket.channel === "whatsapp_oficial"
                              ? ticket.whatsapp?.color || "#25D366"
                              : ticket.channel === "facebook"
                                ? "#4267B2"
                                : ticket.channel === "telegram"
                                  ? "#0088cc"
                                  : "#E1306C"
                        }}
                      >
                        {ticket.whatsapp?.name.toUpperCase()}
                      </span>
                    </Tooltip>
                  ) : null}
                  {
                    <Tooltip
                      title={`📂 Cola: ${ticket.queueId ? ticket.queue?.name : ticket.status === "lgpd" ? "LGPD" : i18n.t("momentsUser.noqueue")}`}
                      arrow
                      placement="top"
                    >
                      <span
                        style={{
                          backgroundColor: ticket.queue?.color || "#7c7c7c"
                        }}
                        className={classes.connectionTag}
                      >
                        {ticket.queueId
                          ? ticket.queue?.name.toUpperCase()
                          : ticket.status === "lgpd"
                            ? "LGPD"
                            : `${i18n.t("momentsUser.noqueue")}`}
                      </span>
                    </Tooltip>
                  }
                </span>

                {/* Plan 1100: Fila 2 exclusiva para el Agente asignado */}
                {ticket?.user && (
                  <span
                    className={classes.secondaryContentSecond}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      overflow: "hidden",
                      marginTop: 3
                    }}
                  >
                    <Tooltip title={`👤 Agente: ${ticket.user?.name || ""}`} arrow placement="top">
                      <span
                        style={{
                          backgroundColor: "#000000"
                        }}
                        className={classes.connectionTag}
                      >
                        👤 {ticket.user?.name.toUpperCase()}
                      </span>
                    </Tooltip>
                  </span>
                )}

                {/* Plan 1100: Fila 3 para información comercial estandarizada uniforme */}
                <span
                  className={classes.secondaryContentSecond}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    overflow: "hidden",
                    gap: 4,
                    marginTop: 3
                  }}
                >
                  {(() => {
                    const kanbanTag = ticket.tags?.find(t => (t.kanban === 1 || t.kanban === true || t.kanban === "1"));
                    const ticketTags = ticket.tags?.filter(t => !(t.kanban === 1 || t.kanban === true || t.kanban === "1")) || [];
                    const brandName = kanbanTag?.kanbanBoard?.brand?.name || ticket.whatsapp?.brand?.name || "";
                    const boardName = kanbanTag?.kanbanBoard?.name || "";
                    const laneName = kanbanTag?.name || "";

                    if (!kanbanTag && ticketTags.length === 0 && !brandName) {
                      return null;
                    }

                    let bubbleBg = kanbanTag?.color || (ticketTags.length > 0 ? (ticketTags[0]?.color || "#455a64") : "rgba(255, 255, 255, 0.08)");
                    const tooltipParts = [];

                    if (brandName) tooltipParts.push(`👑 Marca: ${brandName}`);
                    if (boardName) tooltipParts.push(`⚡ Embudo: ${boardName}`);
                    if (laneName) tooltipParts.push(`🎯 Etapa: ${laneName}`);
                    if (ticketTags.length > 0) {
                      const tagsString = ticketTags.map(t => t.name).join(", ");
                      tooltipParts.push(`🏷️ Etiquetas: ${tagsString}`);
                    }

                    // Plan 1101: Patrón uniforme estandarizado para TODOS los tickets (Marca con corona 👑):
                    // 1. Marca: 👑 [MARCA]
                    // 2. Separador: |
                    // 3. Embudo: ⚡ EMBUDO: [NOMBRE]
                    // 4. Etapa: › [ETAPA]
                    // 5. Continuidad: ...
                    let fullLabel = "";
                    const brandText = brandName ? `👑 ${brandName}` : "";

                    if (boardName && laneName) {
                      const funnelText = `⚡ EMBUDO: ${boardName} › ${laneName}`;
                      fullLabel = brandText ? `${brandText} | ${funnelText} ...` : `${funnelText} ...`;
                    } else if (boardName) {
                      const funnelText = `⚡ EMBUDO: ${boardName}`;
                      fullLabel = brandText ? `${brandText} | ${funnelText} ...` : `${funnelText} ...`;
                    } else if (laneName) {
                      const stageText = `🎯 ${laneName}`;
                      fullLabel = brandText ? `${brandText} | ${stageText} ...` : `${stageText} ...`;
                    } else if (ticketTags.length > 0) {
                      const tagsString = ticketTags.map(t => t.name).join(", ");
                      fullLabel = brandText ? `${brandText} | 🏷️ ${tagsString} ...` : `🏷️ ${tagsString} ...`;
                    } else if (brandText) {
                      fullLabel = brandText;
                    }

                    const tooltipText = tooltipParts.join(" | ");

                    return (
                      <Tooltip key={`ticket-unified-bubble-${ticket.id}`} title={tooltipText} arrow placement="top">
                        <span
                          style={{
                            backgroundColor: bubbleBg,
                            color: "#FFF",
                            height: 18,
                            minHeight: 18,
                            maxHeight: 18,
                            boxSizing: "border-box",
                            padding: "0 6px",
                            fontWeight: "bold",
                            borderRadius: 3,
                            fontSize: "0.58rem",
                            lineHeight: "18px",
                            maxWidth: "100%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            cursor: "pointer",
                            textTransform: "uppercase"
                          }}
                        >
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "18px" }}>
                            {fullLabel}
                          </span>
                        </span>
                      </Tooltip>
                    );
                  })()}
                </span>
              </Typography>

              {/* Badge moved to ListItemSecondaryAction */}
            </span>
          }
        />
        <ListItemSecondaryAction style={{ right: 25 }}>
          {ticket.updatedAt && (
            <>
              <Tooltip title={i18n.t("ticketsList.lastUpdate")}>
                <Typography
                  className={
                    Number(ticket.unreadMessages) > 0
                      ? classes.lastMessageTimeUnread
                      : classes.lastMessageTime
                  }
                  component="span"
                  variant="body2"
                >
                  {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                    <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                  ) : (
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: '1.2' }}>
                      <span>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</span>
                      <span style={{ fontSize: '0.85em', opacity: 0.8 }}>{format(parseISO(ticket.updatedAt), "HH:mm")}</span>
                    </span>
                  )}
                </Typography>
              </Tooltip>
            </>
          )}
        </ListItemSecondaryAction>
        <ListItemSecondaryAction style={{ right: 25 }}>
          {/* Para tickets com status chatbot, mostrar apenas o ícone de spy */}
          {ticket.status === "chatbot" && (
            <span className={classes.secondaryContentSecond}>
              <ButtonWithSpinner
                style={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  border: "none",
                  color: theme.palette.type === "light" ? "blue" : "#FFF",
                  padding: "0px",
                  borderRadius: "50%",
                  right: "15px",
                  fontSize: "0.6rem",
                  bottom: "-30px",
                  minWidth: "2em",
                  width: "auto",
                }}
                variant="contained"
                className={classes.acceptButton}
                size="small"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSpyTicket();
                }}
              >
                <Tooltip title={i18n.t("tickets.spyChatbot")}>
                  <VisibilityIcon />
                </Tooltip>
              </ButtonWithSpinner>
            </span>
          )}

          {/* Para todos os outros status, manter os botões originais */}
          {ticket.status !== "chatbot" && (
            <>
              <span className={classes.secondaryContentSecond}>
                {ticket.status === "pending" &&
                  (ticket.queueId === null || ticket.queueId === undefined) && (
                    <ButtonWithSpinner
                      style={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        border: "none",
                        color: theme.palette.type === "light" ? "green" : "#FFF",
                        padding: "0px",
                        borderRadius: "50%",
                        right: "65px",
                        fontSize: "0.6rem",
                        bottom: "-30px",
                        minWidth: "2em",
                        width: "auto",
                      }}
                      variant="contained"
                      className={classes.acceptButton}
                      size="small"
                      loading={loading}
                      onClick={(e) => handleOpenAcceptTicketWithouSelectQueue()}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                        <Done />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {ticket.status === "pending" && ticket.queueId !== null && (
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: theme.palette.type === "light" ? "green" : "#FFF",
                      padding: "0px",
                      borderRadius: "50%",
                      right: "65px",
                      fontSize: "0.6rem",
                      bottom: "-30px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    loading={loading}
                    onClick={(e) => handleAcepptTicket(ticket.id)}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                      <Done />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              </span>
              <span className={classes.secondaryContentSecond1}>
                {(ticket.status === "pending" ||
                  ticket.status === "open" ||
                  ticket.status === "group") && (
                    <ButtonWithSpinner
                      style={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        border: "none",
                        color: theme.palette.type === "light" ? "purple" : "#FFF",
                        padding: "0px",
                        borderRadius: "50%",
                        right: "40px",
                        position: "absolute",
                        fontSize: "0.6rem",
                        bottom: "-30px",
                        minWidth: "2em",
                        width: "auto",
                      }}
                      variant="contained"
                      className={classes.acceptButton}
                      size="small"
                      loading={loading}
                      onClick={handleOpenTransferModal}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                        <SwapHoriz />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {(ticket.status === "open" || ticket.status === "group") && (
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: theme.palette.type === "light" ? "red" : "#FFF",
                      padding: "0px",
                      bottom: "0px",
                      borderRadius: "50%",
                      right: "15px",
                      fontSize: "0.6rem",
                      bottom: "-30px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    loading={loading}
                    onClick={(e) => handleCloseTicket(ticket.id)}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                      <HighlightOff />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {(ticket.status === "pending" || ticket.status === "lgpd") &&
                  (user.userClosePendingTicket === "enabled" ||
                    user.profile === "admin") && (
                    <ButtonWithSpinner
                      style={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        border: "none",
                        color: theme.palette.type === "light" ? "red" : "#FFF",
                        padding: "0px",
                        bottom: "0px",
                        borderRadius: "50%",
                        right: "15px",
                        fontSize: "0.6rem",
                        bottom: "-30px",
                        minWidth: "2em",
                        width: "auto",
                      }}
                      variant="contained"
                      className={classes.acceptButton}
                      size="small"
                      loading={loading}
                      onClick={(e) => handleCloseIgnoreTicket(ticket.id)}
                    >
                      <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                        <Block />
                      </Tooltip>
                    </ButtonWithSpinner>
                  )}
              </span>
              <span className={classes.secondaryContentSecond}>
                {ticket.status === "closed" && (
                  <ButtonWithSpinner
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      color: theme.palette.type === "light" ? "green" : "#FFF",
                      padding: "0px",
                      bottom: "0px",
                      borderRadius: "50%",
                      right: "15px",
                      fontSize: "0.6rem",
                      bottom: "-30px",
                      minWidth: "2em",
                      width: "auto",
                    }}
                    variant="contained"
                    className={classes.acceptButton}
                    size="small"
                    loading={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenNewTicketModal();
                    }}
                  >
                    <Tooltip title={i18n.t("tickets.buttons.newTicket")}>
                      <Add />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              </span>
            </>
          )}
        </ListItemSecondaryAction>
      </ListItem>

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={async (ticketData) => {
            setOpenFinalizacaoVenda(false);
            try {
              setLoading(true);
              await api.put(`/tickets/${ticket.id}`, {
                ...ticketData,
                sendFarewellMessage: false,
              });
              toast.success(i18n.t("tickets.success.finished", "Atención finalizada con éxito"));
            } catch (err) {
              toastError(err);
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {/* Modal da Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        className={classes.imageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className={classes.imageModalContent}>
          <img
            src={ticket?.contact?.urlPicture}
            alt={ticket?.contact?.name || i18n.t("tickets.contactPhoto")}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TicketListItemCustom;
